import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { v4 } from 'uuid'
import { l, TextArea, TextInputV2 } from '@manychat/manyui'

import { knowledgeEntryResolver } from 'apps/aiAutomation/lib/knowledge'
import { AiAutomationKnowledgeSelectors } from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  KnowledgeEntryType,
  TextEntry,
  TextEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

import { EntryControls } from '../EntryControls/EntryControls'

interface TextEntryFormValues {
  title: string
  text: string
}

interface TextEntryEditorProps {
  entry: TextEntry | null
  onSubmit: (entry: TextEntry | TextEntryDraft) => void
  isLoading: boolean
}

export const TextEntryEditor = ({ entry, onSubmit, isLoading }: TextEntryEditorProps) => {
  const dispatch = useAppDispatch()

  const isLoaded = useAppSelector((state) =>
    AiAutomationKnowledgeSelectors.getIsItemLoaded(state, entry ? entry.id : null),
  )

  const { handleSubmit, formState, register, setValue } = useForm<TextEntryFormValues>({
    defaultValues: {
      text: entry?.data?.text || '',
      title: entry?.title || '',
    },
    resolver: knowledgeEntryResolver,
    mode: 'onChange',
  })

  const submit: SubmitHandler<TextEntryFormValues> = ({ text, title }) => {
    if (entry) {
      onSubmit({
        ...entry,
        title,
        data: {
          ...entry.data,
          text,
        },
      })
    } else {
      onSubmit({
        id: v4(),
        type: KnowledgeEntryType.TEXT,
        title,
        data: {
          text,
        },
      } as TextEntryDraft)
    }
  }

  useEffect(() => {
    if (isLoaded && entry) {
      setValue('text', entry.data.text)
    }
  }, [isLoaded, entry, setValue])

  useEffect(() => {
    dispatch(AiAutomationLayoutActions.showConfirmChanged(formState.isDirty))
  }, [dispatch, formState.isDirty])

  const textProps = register('text')
  const { ref: titleRef, ...titleProps } = register('title')

  return (
    <>
      <TextInputV2
        inputRef={titleRef}
        {...titleProps}
        errorText={formState.errors.title?.message}
        invalid={Boolean(formState.errors.title?.message)}
        readOnly={isLoading}
        className="m-b-lg"
        label={l.translate('What’s this about?')}
        placeholder={l.translate('e.g. Price')}
      />

      <TextArea
        {...textProps}
        invalid={Boolean(formState.errors.text?.message)}
        fullHeight
        label={l.translate('Write what you want AI to know')}
        disabled={isLoading}
        errorText={formState.errors.text?.message}
        data-test-id="ai-automation/knowledge-editor-input"
        placeholder={l.getString(textAreaPlaceholder)}
      />

      <EntryControls entry={entry} onSave={handleSubmit(submit)} isLoading={isLoading} />
    </>
  )
}

const textAreaPlaceholder = l.makeTranslate(`Here's what you can start with:

• Business Name:

• Business Category (e.g., Content Creator, Musician):

• Business Description:

• Website or Social Link (e.g., Instagram, YouTube, Linktree):

• Email:

• Business Hours:`)
